import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { arrayNotEmpty } from "./util";

export enum MessageDialogButton {
  Yes = "Yes",
  No = "No",
  Cancel = "Cancel",
}

export enum MessageDialogButtons {
  Yes = MessageDialogButton.Yes,
  No = MessageDialogButton.No,
  Cancel = MessageDialogButton.Cancel,
  YesNo = Yes + No,
  YesCancel = Yes + Cancel,
  All = Yes + No + Cancel,
}

export enum MessageDialogIcon {
  Info,
  Warning,
  Error,
  Question,
}

export interface MessageDialogCheck {
  label: string;
  value: boolean;
}

export interface MessageDialogOptions<TOrigin> {
  open: boolean;
  title?: string;
  buttons?: MessageDialogButtons;
  icon?: MessageDialogIcon;
  content?: React.ReactNode;
  checks?: MessageDialogCheck[];
  origin?: TOrigin;
}

export interface MessageDialogProps<TOrigin> {
  options: MessageDialogOptions<TOrigin>;
  onSubmit?: (button: MessageDialogButton, origin?: TOrigin, checks?: boolean[]) => void;
}

export const DefaultMessageDialogOptions = <TOrigin extends unknown>(origin?: TOrigin): MessageDialogOptions<TOrigin> => {
  return {
    open: false,
    title: "",
    buttons: MessageDialogButtons.YesNo,
    icon: MessageDialogIcon.Info,
    origin,
  } as MessageDialogOptions<TOrigin>;
};

export const MessageDialog = <TOrigin extends unknown>(props: MessageDialogProps<TOrigin>) => {
  const { options, onSubmit } = props;

  const [checks, setChecks] = useState<boolean[]>([]);

  const show = (button: MessageDialogButton): boolean => ("" + options.buttons).indexOf(button) >= 0;

  const handleClick = (button: MessageDialogButton) => {
    if (onSubmit) onSubmit(button, options.origin, checks);
  };
  const handleCheck = (index: number, value: boolean) => {
    const cloned = [...checks];
    cloned[index] = value;
    setChecks(cloned);
  };
  useEffect(() => {
    if (options.checks && arrayNotEmpty(options.checks)) {
      setChecks(options.checks.map((check) => check.value ?? false));
    } else {
      setChecks([]);
    }
  }, [options]);

  return (
    <Dialog open={options.open} maxWidth="sm" fullWidth>
      <DialogTitle>{options.title}</DialogTitle>
      <DialogContent>
        <Box>{options.content}</Box>
        {options.checks && arrayNotEmpty(options.checks) && (
          <FormGroup>
            <Stack spacing={1} direction="row">
              {options.checks.map((check, index) => (
                <FormControlLabel key={index} label={check.label} control={<Checkbox key={index} checked={checks[index] ?? false} onChange={(e, c) => handleCheck(index, c)} />} />
              ))}
            </Stack>
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions>
        {show(MessageDialogButton.Yes) && (
          <Button variant="contained" onClick={() => handleClick(MessageDialogButton.Yes)}>
            Yes
          </Button>
        )}
        {show(MessageDialogButton.No) && (
          <Button variant="contained" onClick={() => handleClick(MessageDialogButton.No)}>
            No
          </Button>
        )}
        {show(MessageDialogButton.Cancel) && (
          <Button variant="contained" onClick={() => handleClick(MessageDialogButton.Cancel)}>
            Cancel
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
